import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { GET_LLM_EXTRACTOR_INPUT_OPTIONS, GET_LLM_EXTRACTOR_OUTPUT_OPTIONS } from './queries.ts';
import { DEFAULT_LINE_ITEM_TYPE } from './constants.ts';
import { RuleLevel } from './components/fineTuning/rules.ts';
import { GROUP_SEPARATOR } from './pages/Assistance/useDocumentConfig.ts';

// Sorted names of the fields a rule of the level can write to, from the channel-aware backend list; line item rules
// only write to the default item type
export const useLlmExtractorOutputFieldNames = (
    channelId: string | undefined,
    gqlDocumentType: string | undefined,
    level: RuleLevel,
    skip = false
): string[] => {
    const { data } = useQuery(GET_LLM_EXTRACTOR_OUTPUT_OPTIONS, {
        variables: { channelId, documentType: gqlDocumentType },
        skip: skip || !channelId || !gqlDocumentType,
    });

    return useMemo(
        () =>
            (data?.llmExtractorOutput || [])
                .filter(
                    (option) =>
                        option.isConfiguredForChannel &&
                        (level === 'header'
                            ? option.applicableForHeader
                            : option.name.startsWith(`${DEFAULT_LINE_ITEM_TYPE}${GROUP_SEPARATOR}`))
                )
                .map(({ name }) => name)
                .toSorted(),
        [data, level]
    );
};

// The inputs a rule of the level can read; inputs whose document field isn't configured for the channel are left out
export const useLlmExtractorInputs = (
    channelId: string | undefined,
    gqlDocumentType: string | undefined,
    level: RuleLevel,
    skip = false
) => {
    const { data } = useQuery(GET_LLM_EXTRACTOR_INPUT_OPTIONS, {
        variables: { channelId, documentType: gqlDocumentType },
        skip: skip || !channelId || !gqlDocumentType,
    });

    return useMemo(
        () =>
            (data?.llmExtractorInput ?? []).filter(
                (option) =>
                    option.isConfiguredForChannel &&
                    (level === 'header' ? option.applicableForHeader : option.applicableForLineItems)
            ),
        [data, level]
    );
};

// the header and line item labels share some names, so a field's own list is looked up first
export const getFieldLabelKeys = (fieldName: string) => {
    const field = fieldName.replaceAll(`${DEFAULT_LINE_ITEM_TYPE}${GROUP_SEPARATOR}`, '');
    const itemKey = `itemsView.fieldNames.${field}`;
    const headerKey = `headerView.fieldNames.${field}`;
    const isLineItemField = fieldName.startsWith(`${DEFAULT_LINE_ITEM_TYPE}${GROUP_SEPARATOR}`);
    return { field, keys: isLineItemField ? [itemKey, headerKey] : [headerKey, itemKey] };
};

// Readable label of a field name like "delivery_address__city" ("Delivery address (City)") or
// "line_items__quantity"
export const useGetFieldLabel = () => {
    const { t, i18n } = useTranslation();

    return useCallback(
        (fieldName: string) => {
            const { field: cleanFieldName, keys } = getFieldLabelKeys(fieldName);
            const parentFieldName = cleanFieldName.includes(GROUP_SEPARATOR)
                ? cleanFieldName.split(GROUP_SEPARATOR)[0]
                : undefined;
            const key = keys.find((labelKey) => i18n.exists(`assistance:${labelKey}`));

            if (key?.startsWith('headerView') && parentFieldName) {
                return `${t(`assistance:headerView.fieldNames.${parentFieldName}`)} (${t(`assistance:${key}`)})`;
            } else if (key) {
                return t(`assistance:${key}`);
            } else if (i18n.exists(`config:configPage.document.validationCheckNames.${cleanFieldName}`)) {
                return t(`config:configPage.document.validationCheckNames.${cleanFieldName}`);
            }
            return cleanFieldName;
        },
        [t, i18n]
    );
};

// Readable label of an extractor input: a document field's label, or a name from
// config:configPage.fineTuning.inputNames; inputs from a customer's customization keep their backend name
export const useGetInputLabel = () => {
    const { t, i18n } = useTranslation('config');
    const getFieldLabel = useGetFieldLabel();

    return useCallback(
        (input: { name: string; fieldPath?: string | null }) => {
            if (input.fieldPath) return getFieldLabel(input.fieldPath.replaceAll('.', GROUP_SEPARATOR));
            return i18n.exists(`config:configPage.fineTuning.inputNames.${input.name}`)
                ? t(`configPage.fineTuning.inputNames.${input.name}`)
                : input.name;
        },
        [t, i18n, getFieldLabel]
    );
};
